import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Clock, FlaskConical, Minus, RefreshCw, Send, TrendingDown, TrendingUp, Trophy } from 'lucide-react';
import { api } from 'core/axios/axios';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { IconTile } from 'components/ui/icon-tile';
import { Skeleton } from 'components/ui/skeleton';
import { humanizeStatus, tone, type Tone } from 'components/ui/tone';
import { ABCampaignStats, getVariantStats, hasVariantId, isABCampaign, variantMetrics } from 'core/features/dashboard/abTesting';
const METRICS = variantMetrics({});
/** Metrics where a higher number is the worse outcome. */
const INVERTED = new Set(['Bounced']);
const HEADLINE = 'Open Rate';

type Letter = 'A' | 'B';
const LETTER_TONE: Record<Letter, Tone> = { A: 'brand', B: 'violet' };

function formatMetric(value: number | null | undefined, rate: boolean): string {
    if (value === null || value === undefined) return '—';
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${rate ? '%' : ''}`;
}

function metricValues(data: Record<string, any>): Map<string, number | null> {
    return new Map(variantMetrics(data).map((metric) => [metric.label, metric.value]));
}

/* ── Variant statistics ───────────────────────────────────────────────── */

interface VariantState {
    /** The campaign reported a usable variant id for this slot */
    present: boolean;
    loading: boolean;
    error: boolean;
    values: Map<string, number | null> | null;
    retry: () => void;
}

function useVariantStats(
    variantId: string | number | null | undefined,
    variantType: 'TEST' | 'WINNER',
    refresh: number
): VariantState {
    const [retryCount, setRetryCount] = useState(0);
    const [result, setResult] = useState<{ key: string; data?: Record<string, any>; error?: boolean } | null>(null);
    const present = hasVariantId(variantId);
    const key = `${variantId}:${variantType}:${refresh}:${retryCount}`;

    useEffect(() => {
        if (!present) return;
        const controller = new AbortController();
        getVariantStats(variantId as string | number, variantType, controller.signal)
            .then((data) => {
                if (controller.signal.aborted) return;
                if (!data || typeof data !== 'object' || Array.isArray(data)) throw new Error('Invalid statistics response');
                setResult({ key, data });
            })
            .catch(() => {
                if (!controller.signal.aborted) setResult({ key, error: true });
            });
        return () => controller.abort();
    }, [present, variantId, variantType, key]);

    const current = result?.key === key ? result : null;
    return {
        present,
        loading: present && !current,
        error: Boolean(current?.error),
        values: current?.data ? metricValues(current.data) : null,
        retry: () => setRetryCount((value) => value + 1),
    };
}

/* ── Difference ───────────────────────────────────────────────────────── */

function Difference({ a, b, inverted }: { a: number | null | undefined; b: number | null | undefined; inverted: boolean }) {
    const comparable =
        typeof a === 'number' && typeof b === 'number' && Number.isFinite(a) && Number.isFinite(b) && a !== 0;
    if (!comparable) {
        return (
            <span className="text-meta text-content-subtle" title="Both variants need a reported value to compare">
                —
            </span>
        );
    }
    const change = ((b - a) / Math.abs(a)) * 100;
    const flat = Math.abs(change) < 0.05;
    const better = inverted ? change < 0 : change > 0;
    const chip = tone(flat ? 'neutral' : better ? 'success' : 'danger').chip;
    const Icon = flat ? Minus : change > 0 ? TrendingUp : TrendingDown;
    return (
        <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-meta font-semibold tabular-nums', chip)}>
            <Icon className="w-3 h-3" aria-hidden="true" />
            {flat ? '0%' : `${change > 0 ? '+' : '-'}${Math.abs(change).toFixed(1)}%`}
        </span>
    );
}

/* ── Comparison ───────────────────────────────────────────────────────── */

function ValueCell({ state, label, rate, share, accent }: {
    state: VariantState; label: string; rate: boolean; share: number; accent: string;
}) {
    const value = state.values?.get(label) ?? null;
    return (
        <td className="py-3 px-3 align-middle">
            <div className="flex flex-col items-end gap-1.5">
                {state.loading ? (
                    <Skeleton className="h-4 w-16 rounded" />
                ) : (
                    <span className="text-ui-lg font-semibold tabular-nums text-content">
                        {state.values ? formatMetric(value, rate) : '—'}
                    </span>
                )}
                <span aria-hidden="true" className="block h-1 w-full max-w-[7rem] overflow-hidden rounded-full bg-surface-sunken">
                    <span className={cn('block h-full rounded-full transition-[width] duration-500', accent)} style={{ width: `${share}%` }} />
                </span>
            </div>
        </td>
    );
}

function VariantCard({ letter, state, isWinner }: { letter: Letter; state: VariantState; isWinner: boolean }) {
    const tokens = tone(LETTER_TONE[letter]);
    const sent = state.values?.get('Sent') ?? null;
    const headline = state.values?.get(HEADLINE) ?? null;
    return (
        <div className={cn(
            'relative overflow-hidden rounded-2xl border p-4 sm:p-5',
            isWinner ? 'border-success-border bg-success-subtle' : 'border-line bg-surface'
        )}>
            <span aria-hidden="true" className={cn('absolute inset-x-0 top-0 h-1', tokens.accent)} />
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                    <span className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-title font-bold', tokens.tile)}>
                        {letter}
                    </span>
                    <div className="min-w-0">
                        <p className="text-ui-lg font-semibold text-content">Variant {letter}</p>
                        <p className="text-meta text-content-muted">
                            {state.loading ? 'Loading…' : sent === null ? 'Recipients not reported' : `${sent.toLocaleString()} sent`}
                        </p>
                    </div>
                </div>
                {isWinner && (
                    <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-1 text-micro font-bold uppercase tracking-wide', tone('success').chip)}>
                        <Trophy className="w-3 h-3" aria-hidden="true" />
                        Winner
                    </span>
                )}
            </div>

            <div className="mt-4 min-h-[3.25rem]">
                {state.loading ? (
                    <Skeleton className="h-9 w-28 rounded-lg" />
                ) : state.error ? (
                    <div role="alert" className="flex flex-wrap items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 text-ui text-content-secondary">
                            <AlertTriangle className="w-4 h-4 text-danger" aria-hidden="true" />
                            Results didn’t load.
                        </span>
                        <Button variant="ghost" size="sm" onClick={state.retry}>
                            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                            Retry Variant {letter}
                        </Button>
                    </div>
                ) : (
                    <>
                        <p className="text-display-sm font-bold tabular-nums leading-none text-content">
                            {formatMetric(headline, true)}
                        </p>
                        <p className="mt-1.5 text-meta text-content-muted">Open rate within the test group</p>
                    </>
                )}
            </div>
        </div>
    );
}

/* ── Follow-up send ───────────────────────────────────────────────────── */

function WinnerAudience({ state, winnerName }: { state: VariantState; winnerName: string | null }) {
    return (
        <section
            aria-label="Winner audience"
            className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden"
        >
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3.5 sm:px-5">
                <div className="flex min-w-0 items-center gap-3">
                    <IconTile tone="success" size="md">
                        <Send strokeWidth={2} />
                    </IconTile>
                    <div className="min-w-0">
                        <h4 className="text-title-sm font-semibold text-content">Follow-up send</h4>
                        <p className="text-meta text-content-muted">
                            {winnerName ? `${winnerName} going to the audience held back from the test` : 'Starts once a winner is selected'}
                        </p>
                    </div>
                </div>
                {state.error && (
                    <Button variant="ghost" size="sm" onClick={state.retry}>
                        <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
                        Retry Winner audience
                    </Button>
                )}
            </header>

            <div className="p-4 sm:p-5">
                {!state.present ? (
                    <p className="text-ui-lg text-content-muted">
                        Waiting for winner selection. Nothing has been sent to the remaining audience yet.
                    </p>
                ) : state.error ? (
                    <p role="alert" className="text-ui-lg text-content-secondary">
                        Unable to load the follow-up results.
                    </p>
                ) : (
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4 lg:grid-cols-7">
                        {METRICS.map((metric) => (
                            <div key={metric.label} className="min-w-0">
                                <dt className="truncate text-micro font-semibold uppercase tracking-wide text-content-muted">
                                    {metric.label}
                                </dt>
                                <dd className="mt-1 text-title font-semibold tabular-nums text-content">
                                    {state.loading ? (
                                        <Skeleton className="h-5 w-14 rounded" />
                                    ) : (
                                        formatMetric(state.values?.get(metric.label) ?? null, metric.rate)
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>
                )}
            </div>
        </section>
    );
}

/* ── Results ──────────────────────────────────────────────────────────── */

export interface ABResolution {
    isABTest: boolean;
    hasWinner: boolean;
    status?: string;
}

export default function ABCampaignResults({ campaignId, initialStats, onResolved }: {
    campaignId: string | number;
    initialStats?: ABCampaignStats;
    /** Reports what this campaign turned out to be, so the picker can label it */
    onResolved?: (resolution: ABResolution) => void;
}) {
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState<{ key: string; data?: ABCampaignStats; error?: boolean } | null>(null);
    const key = `${campaignId}:${refresh}`;

    useEffect(() => {
        const controller = new AbortController();
        if (initialStats && refresh === 0) {
            setState({ key, data: initialStats });
        } else {
            api.get('campaigns/stats', { params: { cid: campaignId, paginator: 0 }, signal: controller.signal })
                .then(({ data }) => {
                    if (!controller.signal.aborted) setState({ key, data });
                })
                .catch(() => {
                    if (!controller.signal.aborted) setState({ key, error: true });
                });
        }
        return () => controller.abort();
    }, [campaignId, initialStats, refresh, key]);

    const current = state?.key === key ? state : null;
    const data = current?.data;
    const winnerId = data?.winning_variant_id;
    const winnerName = hasVariantId(winnerId)
        ? String(winnerId) === String(data?.variant_a?.variant_id) ? 'Variant A'
            : String(winnerId) === String(data?.variant_b?.variant_id) ? 'Variant B' : `Variant ${winnerId}`
        : null;

    // Reported to the picker so a campaign the user already opened carries its
    // outcome in the list, instead of having to be opened again to find out.
    const resolvedRef = useRef(onResolved);
    resolvedRef.current = onResolved;
    useEffect(() => {
        if (!data) return;
        resolvedRef.current?.({
            isABTest: isABCampaign(data),
            hasWinner: hasVariantId(data.winning_variant_id),
            status: data.status,
        });
    }, [data]);

    const variantA = useVariantStats(data?.variant_a?.variant_id, 'TEST', refresh);
    const variantB = useVariantStats(data?.variant_b?.variant_id, 'TEST', refresh);
    const winner = useVariantStats(winnerId, 'WINNER', refresh);

    const settled = Boolean(winnerName);
    const verdictTone = settled ? 'success' : 'violet';

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
                <h3 className="inline-flex items-center gap-2 text-meta font-semibold uppercase tracking-wider text-content-muted">
                    <FlaskConical className="w-3.5 h-3.5" aria-hidden="true" />
                    A/B campaign results
                </h3>
                <Button variant="secondary" size="sm" disabled={!current} onClick={() => setRefresh((value) => value + 1)}>
                    <RefreshCw aria-hidden="true" className={cn('w-4 h-4', !current && 'animate-spin')} />
                    Refresh results
                </Button>
            </div>

            {!current ? (
                <div className="space-y-4" role="status" aria-label="Loading A/B campaign">
                    <Skeleton className="h-24 w-full rounded-2xl" />
                    <div className="grid gap-4 sm:grid-cols-2">
                        <Skeleton className="h-32 w-full rounded-2xl" />
                        <Skeleton className="h-32 w-full rounded-2xl" />
                    </div>
                    <Skeleton className="h-56 w-full rounded-2xl" />
                </div>
            ) : current.error ? (
                <div role="alert" className="rounded-2xl border border-danger-border bg-danger-subtle p-5 sm:p-6">
                    <div className="flex items-start gap-3">
                        <IconTile tone="danger" size="md">
                            <AlertTriangle strokeWidth={2} />
                        </IconTile>
                        <div className="min-w-0">
                            <p className="text-title-sm font-semibold text-content">We couldn’t load this campaign</p>
                            <p className="mt-1 text-ui-lg text-content-secondary">
                                The results request didn’t come back. Use Refresh results to try again.
                            </p>
                        </div>
                    </div>
                </div>
            ) : !isABCampaign(data) ? (
                <div className="rounded-2xl border border-line bg-surface px-6 py-12 text-center shadow-card">
                    <IconTile tone="neutral" size="xl" className="mx-auto mb-4">
                        <FlaskConical strokeWidth={1.75} />
                    </IconTile>
                    <h4 className="text-title font-semibold text-content">No A/B test for this campaign</h4>
                    <p className="mx-auto mt-2 max-w-md text-ui-lg leading-relaxed text-content-muted">
                        This campaign was sent as a single version, so there is nothing to compare. Pick a campaign that was
                        set up with two variants, or open the dashboard for its regular delivery statistics.
                    </p>
                </div>
            ) : (
                <>
                    <section className={cn(
                        'relative overflow-hidden rounded-2xl border p-5 sm:p-6',
                        settled ? 'border-success-border bg-success-subtle' : 'border-violet-border bg-violet-subtle'
                    )}>
                        <span aria-hidden="true" className="pointer-events-none absolute -right-20 -top-24 h-56 w-56 rounded-full bg-white/40 blur-3xl dark:bg-white/5" />
                        <div className="relative flex items-start gap-4">
                            <IconTile tone={verdictTone} size="lg" solid>
                                {settled ? <Trophy strokeWidth={2} /> : <Clock strokeWidth={2} />}
                            </IconTile>
                            <div className="min-w-0 flex-1">
                                <p className="text-meta font-semibold uppercase tracking-wider text-content-muted">
                                    {settled ? 'Result' : 'In progress'}
                                </p>
                                <p role="status" className="mt-1 text-title-lg font-bold tracking-tight text-content sm:text-display-sm">
                                    {settled ? `Winner: ${winnerName}` : 'Winner pending'}
                                </p>
                                <p className="mt-2 max-w-2xl text-ui-lg leading-relaxed text-content-secondary">
                                    {settled
                                        ? 'The comparison below covers the test group only. The winning version’s send to the remaining audience is reported separately.'
                                        : 'Evaluation has not selected a winner yet. Both test groups are shown below while you wait — refresh results to check again.'}
                                </p>

                                <dl className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-meta">
                                    {data?.status && (
                                        <div className="flex items-center gap-1.5">
                                            <dt className="text-content-muted">Status</dt>
                                            <dd className="font-semibold text-content">{humanizeStatus(data.status)}</dd>
                                        </div>
                                    )}
                                    {typeof data?.test_percentage === 'number' && (
                                        <div className="flex items-center gap-1.5">
                                            <dt className="text-content-muted">Test audience</dt>
                                            <dd className="font-semibold tabular-nums text-content">{data.test_percentage}%</dd>
                                        </div>
                                    )}
                                    {typeof data?.evaluation_hours === 'number' && (
                                        <div className="flex items-center gap-1.5">
                                            <dt className="text-content-muted">Evaluation window</dt>
                                            <dd className="font-semibold tabular-nums text-content">{data.evaluation_hours}h</dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        </div>
                    </section>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <VariantCard letter="A" state={variantA} isWinner={winnerName === 'Variant A'} />
                        <VariantCard letter="B" state={variantB} isWinner={winnerName === 'Variant B'} />
                    </div>

                    <section className="rounded-2xl border border-line bg-surface shadow-card overflow-hidden">
                        <header className="border-b border-line px-4 py-3.5 sm:px-5">
                            <h4 className="text-title-sm font-semibold text-content">Head to head</h4>
                            <p className="mt-0.5 text-meta text-content-muted">
                                Test recipients only. The difference column reads Variant B against Variant A.
                            </p>
                        </header>
                        <div className="overflow-x-auto scrollbar-slim">
                            <table className="w-full min-w-[34rem] border-collapse">
                                <caption className="sr-only">Variant A and Variant B compared across every reported metric</caption>
                                <thead>
                                    <tr className="border-b border-line">
                                        <th scope="col" className="py-2.5 pl-4 pr-3 text-left text-micro font-semibold uppercase tracking-wide text-content-muted sm:pl-5">
                                            Metric
                                        </th>
                                        <th scope="col" className="py-2.5 px-3 text-right text-micro font-semibold uppercase tracking-wide text-content-muted">
                                            Variant A
                                        </th>
                                        <th scope="col" className="py-2.5 px-3 text-right text-micro font-semibold uppercase tracking-wide text-content-muted">
                                            Variant B
                                        </th>
                                        <th scope="col" className="py-2.5 pl-3 pr-4 text-right text-micro font-semibold uppercase tracking-wide text-content-muted sm:pr-5">
                                            B vs A
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {METRICS.map((metric) => {
                                        const a = variantA.values?.get(metric.label) ?? null;
                                        const b = variantB.values?.get(metric.label) ?? null;
                                        const peak = Math.max(a ?? 0, b ?? 0);
                                        const share = (value: number | null) => (peak > 0 && value !== null ? (value / peak) * 100 : 0);
                                        return (
                                            <tr key={metric.label} className="border-b border-line-subtle last:border-0">
                                                <th scope="row" className="py-3 pl-4 pr-3 text-left text-ui-lg font-medium text-content-secondary sm:pl-5">
                                                    {metric.label}
                                                </th>
                                                <ValueCell state={variantA} label={metric.label} rate={metric.rate} share={share(a)} accent={tone('brand').accent} />
                                                <ValueCell state={variantB} label={metric.label} rate={metric.rate} share={share(b)} accent={tone('violet').accent} />
                                                <td className="py-3 pl-3 pr-4 text-right sm:pr-5">
                                                    <Difference a={a} b={b} inverted={INVERTED.has(metric.label)} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <WinnerAudience state={winner} winnerName={winnerName} />

                    <p className="text-meta text-content-muted">
                        Test results and follow-up results are reported separately by the API and are never combined here. A
                        dash means the metric was not provided.
                    </p>
                </>
            )}
        </div>
    );
}
